/**
 * IonCapabilityGallery — the opt-in capability gallery (Guided Tour
 * Redesign, Part D3). A card grid showing the range of things Ion can do,
 * each card playing a RECORDING of a real turn (Part D1's playRecordedFlow)
 * through the exact same dispatch path a live turn uses — same tool-call
 * chips, same artifacts, same "Ion: ... · REVERT" attribution card
 * (Part D2) — with a persistent marker (the `example` flag, reused from S4)
 * that it's a recording, not a live answer.
 *
 * Mounted inline by IonSurface, same drawer pattern as
 * ConversationHistoryDrawer.jsx: `position:absolute; inset:0` over
 * `.ion-panel__surface` (see ion-capability-gallery.css). Its own open/close
 * state (`capabilityGalleryOpen`, capabilityGalleryState.js) is a signal
 * rather than local state because the gallery has entry points OUTSIDE
 * IonSurface's tree (HelpMenuButton) as well as inside it (IonHome,
 * IonIntroHandoverChips) — see that file's doc comment.
 *
 * Honesty is the point of this component, not a decoration on top of it:
 * `CAPABILITY_GALLERY_CARDS` (ionCapabilityRecordings.js) lists all SIX
 * planned capabilities, but only cards with an ACTUAL entry in
 * `CAPABILITY_RECORDINGS` render a Play action — the rest render visibly
 * disabled with an honest "Recording coming soon" label. Never omit the
 * unavailable ones and never make them clickable-but-inert; both would hide
 * from BA how much of the gallery is actually real yet.
 */
import { useLocation } from 'wouter-preact';
import { chatStreaming, ionSurface, collapseFromFull } from '../../state/chatStore.js';
import { commandProvenance } from '../../state/commandLayer.js';
import {
  capabilityGalleryOpen,
  closeCapabilityGallery,
  captureGallerySessionBaselineIfNeeded,
  restoreGallerySessionBaseline,
  gallerySessionBaselineHeld,
} from '../../state/capabilityGalleryState.js';
import { playRecordedFlow } from '../../hooks/useChatStream.js';
import {
  CAPABILITY_GALLERY_CARDS,
  CAPABILITY_RECORDINGS,
} from '../../services/ionCapabilityRecordings.js';
import {
  IconSliders,
  IconScatterPlot,
  IconCompare,
  IconInfo,
  IconTarget,
  IconFileText,
} from '../shared/Icons.jsx';

// Icon per capability, keyed by card id. Lives HERE rather than in
// ionCapabilityRecordings.js so that module stays plain data (ids, prompts,
// fixtures) with no JSX dependency — same split IonHome.jsx uses for its
// ENTRY_TILES. IconTarget is deliberately the same mark IonHome puts on "Find
// the right cell", since `guided-selection` demonstrates exactly that flow.
//
// A card id with no entry here renders without an icon rather than crashing on
// `<undefined />`, so adding a seventh capability degrades visibly instead of
// blanking the gallery.
const CARD_ICONS = {
  'steer-explorer': IconSliders,
  'plot-it': IconScatterPlot,
  'compare-cells': IconCompare,
  'explain-concept': IconInfo,
  'guided-selection': IconTarget,
  'build-report': IconFileText,
};

export function IonCapabilityGallery() {
  const [, navigate] = useLocation();
  const open = capabilityGalleryOpen.value;
  if (!open) return null;

  // Same signal ChatInput's `canSend` gates sending on (chatStore.js) — a
  // click here must not start a second stream on top of one already live,
  // mirroring IonIntroHandoverChips.jsx's identical guard rather than
  // inventing a second "is Ion busy" check. Deliberately NOT reusing
  // `canSend` wholesale: its other two conditions (non-empty draft input,
  // quota headroom) don't apply to a recorded replay — a replay never
  // reaches the network, so it can't be blocked by quota, and there is no
  // draft text to be non-empty.
  const streaming = chatStreaming.value;
  const hasBaseline = gallerySessionBaselineHeld.value;

  async function handlePlay(id) {
    if (streaming) return;
    captureGallerySessionBaselineIfNeeded();
    const wasFullpage = ionSurface.peek() === 'full';
    // Snapshotted so the check below tests what THIS replay committed. The
    // signal is global and keeps the last commit, so `=== 'commit'` alone would
    // also fire for a pure question-and-answer recording played after a
    // steering one.
    const provenanceBefore = commandProvenance.peek();
    // Close FIRST, then play. The gallery is an opaque overlay across the whole
    // panel, so leaving it up meant the replay it started was completely
    // hidden: a click on Play produced no visible change beyond a "Back to my
    // view" button appearing, while the messages streamed in and the Explorer
    // was re-filtered behind it. Watching Ion work is the entire point of the
    // card, so the gallery has to get out of the way for it.
    //
    // Closing does not interrupt anything — playRecordedFlow writes to the
    // message store and the command layer, neither of which is owned by this
    // component. Re-opening the gallery still offers "Back to my view",
    // and the Explorer's own "Ion: … · Revert" attribution card offers the
    // same undo where the change actually landed.
    closeCapabilityGallery();
    await playRecordedFlow(id);

    // If the replay STEERED the Explorer, put the Explorer on screen.
    //
    // Without this the flagship card is a lie in the geometry where the gallery
    // is most likely to be opened: in fullpage, Ion says "The Explorer is now
    // filtered to NMC cells…" while the user looks at a chat column and an
    // empty artifact panel. The filters really were applied — to a surface that
    // is not rendered. "Ion changes the real UI, not just describing it" needs
    // the real UI to be visible, so collapse to the docked panel and navigate
    // to the Explorer: Ion's explanation stays on the right, the changed grid
    // appears on the left. That is also exactly how the product is meant to be
    // used.
    //
    // Keyed on commandProvenance rather than on the card id or a pre-scan of
    // the fixture: it reflects what the command layer ACTUALLY committed this
    // turn, so a recording that gains or loses explorer commands later cannot
    // fall out of step with this. Recordings that only answer (explain,
    // compare, report) commit nothing and are left where they are — nothing to
    // go and look at.
    const provenanceAfter = commandProvenance.peek();
    const steeredNow =
      provenanceAfter?.command === 'commit' && provenanceAfter !== provenanceBefore;
    if (wasFullpage && steeredNow) {
      collapseFromFull();
      navigate('/explorer');
    }
  }

  return (
    <div class="ion-gallery" role="dialog" aria-label="Ion capability gallery">
      {/* __inner carries the centred measure. The gallery fills the whole
          panel (it's the scroll container and paints the background), but its
          CONTENT must not run the full width of a large monitor — without this
          wrapper it sat flush left with most of the screen empty beside it. */}
      <div class="ion-gallery__inner">
        <div class="ion-gallery__header">
          <h3 class="ion-gallery__title">What Ion can do</h3>
          <button
            type="button"
            class="ion-gallery__close"
            onClick={closeCapabilityGallery}
            aria-label="Close gallery"
          >
            ✕
          </button>
        </div>

        <p class="ion-gallery__intro">
          Each card replays a recording of a real Ion turn — labelled, and it costs nothing. Ion
          really drives the Explorer, so you can always come back to your own view.
        </p>

        <ul class="ion-gallery__grid">
          {CAPABILITY_GALLERY_CARDS.map((card) => {
            const available = Boolean(CAPABILITY_RECORDINGS[card.id]);
            const Icon = CARD_ICONS[card.id];
            return (
              <li
                key={card.id}
                class={`ion-gallery__card${available ? '' : ' ion-gallery__card--soon'}`}
              >
                {Icon && <Icon size={20} className="ion-gallery__card-icon" />}
                <span class="ion-gallery__card-label">{card.label}</span>
                <span class="ion-gallery__card-desc">{card.description}</span>
                {available ? (
                  <button
                    type="button"
                    class="ion-gallery__play"
                    disabled={streaming}
                    onClick={() => handlePlay(card.id)}
                  >
                    {streaming ? 'Playing…' : 'Play'}
                  </button>
                ) : (
                  <span class="ion-gallery__soon">Recording coming soon</span>
                )}
              </li>
            );
          })}
        </ul>

        {hasBaseline && (
          <button
            type="button"
            class="ion-gallery__restore"
            onClick={restoreGallerySessionBaseline}
          >
            Back to my view
          </button>
        )}
      </div>
    </div>
  );
}
